import React from 'react';
import { useQueries, useQuery } from 'react-query';
import {
  useProfile,
  useFilteredBalances,
  usePrimaryVault,
  CURRENCY_BSV,
  useStaticResources,
  useIntl,
  useTokenizedApi,
  INSTRUMENT_TYPE_CURRENCY,
  INSTRUMENT_TYPE_CREDIT_NOTE,
} from '@tokenized/sdk-react-private';
import { Instrument } from '@tokenized/sdk-js-private';
import {
  useActivePersonalProfile,
  useLockboxBalances,
  useActiveEntityId,
  useInstrumentContractDetails,
  useVisibleInstrumentContractDetails,
} from '#modules/api/User';
import { EntityContext } from '#modules/context/EntityContext';
import { BankingContext } from '#modules/context/BankingContext';
import { useVisibleInstruments } from '#modules/api/Banking';

/** @category Instruments */
export function useLiquidNobleInstrumentWithDetails(
  instrumentDescription,
  { useSellRate = false } = {},
) {
  const intl = useIntl();
  const currencies = useStaticResources()?.currencies;
  const visibleInstruments = useVisibleInstruments();

  let instrument = new Instrument(instrumentDescription)
    .withIntl(intl)
    .withCurrenciesStaticResources(currencies);

  const [instrumentContractDetails] = useInstrumentContractDetails(
    instrument.instrumentId,
  );

  const matchedInstrument = visibleInstruments?.find(
    (match) => match.instrument_id === instrument.instrumentId,
  );

  instrument = instrument.withInstrumentDetails(instrumentContractDetails);

  const exchangeRate = useSellRate
    ? matchedInstrument?.unit_value_sell
    : matchedInstrument?.unit_value;
  instrument = instrument.withDisplayCurrencyRate(
    matchedInstrument?.rate_currency_code,
    exchangeRate ?? 1,
  );

  return instrument;
}

export function useRelevantInstrumentListWithDetails(
  instrumentDescriptions,
  profileId,
  matchInstruments,
) {
  return useLiquidNobleInstrumentListWithDetails(
    instrumentDescriptions,
    profileId,
    matchInstruments,
  );
}

export function useRelevantInstrumentWithDetails(
  instrumentDescription,
  profileId,
  instrumentParams = {},
) {
  return useLiquidNobleInstrumentWithDetails(
    instrumentDescription,
    instrumentParams,
  );
}

export function useInstrumentListWithDetails(
  instrumentDescriptions,
  profileId,
  matchInstruments,
) {
  const tokenizedApi = useTokenizedApi();
  const intl = useIntl();

  const personalProfile = useActivePersonalProfile();
  profileId = profileId || personalProfile?.id;
  const displayCurrencyCode = useProfile(profileId)?.displayCurrency;
  const currencies = useStaticResources()?.currencies;

  let instruments = [];
  for (const options of instrumentDescriptions || []) {
    instruments.push(
      new Instrument(options)
        .withIntl(intl)
        .withCurrenciesStaticResources(currencies),
    );
  }

  // This allows fully-functional Instrument objects to be constructed from
  // contract instrument draft IDs (the instrument details are extracted
  // from contract draft data in the query cache)
  instruments = instruments.map((instrument) =>
    tokenizedApi.contracts.setInstrumentDetailsFromContractDraft(instrument),
  );

  const instrumentDetails = useQueries(
    instruments.map((instrument) =>
      tokenizedApi.treasury.instrumentDetailsQuery(
        instrument.instrumentIdToQuery,
      ),
    ),
  ).map((result) => result?.data);
  instruments = instruments.map((instrument, index) =>
    instrument.withInstrumentDetails(instrumentDetails[index]),
  );

  const exchangeRates = useQuery(
    tokenizedApi.treasury.allExchangeRatesQuery(),
  ).data;

  instruments = instruments.map((instrument) => {
    const matchedInstrument = matchInstruments?.find(
      (match) => match.instrument_id === instrument.instrumentId,
    );
    const exchangeRate =
      matchedInstrument?.unit_value ??
      exchangeRates?.findRate(
        instrument.exchangeRateToQuery,
        displayCurrencyCode,
      );
    return instrument.withDisplayCurrencyRate(
      displayCurrencyCode,
      exchangeRate,
    );
  });

  return instruments;
}

/** @category Instruments */
export function useLiquidNobleInstrumentListWithDetails(
  instrumentDescriptions,
) {
  const visibleInstruments = useVisibleInstruments();
  const [contractDetails] = useVisibleInstrumentContractDetails();
  const tokenizedApi = useTokenizedApi();
  const intl = useIntl();

  const currencies = useStaticResources()?.currencies;

  let instruments = [];
  for (const options of instrumentDescriptions || []) {
    const instrument = new Instrument(options)
      .withIntl(intl)
      .withCurrenciesStaticResources(currencies);

    if (instrument.instrumentId) {
      const matchedInstrument = visibleInstruments?.find(
        (match) => match.instrument_id === instrument.instrumentId,
      );
      if (!matchedInstrument) {
        continue;
      }
    }
    instruments.push(instrument);
  }

  const instrumentDetails = useQueries(
    instruments.map((instrument) =>
      tokenizedApi.treasury.instrumentDetailsQuery(
        instrument.instrumentIdToQuery,
      ),
    ),
  ).map((result) => result?.data);

  instruments = instruments.map((instrument, index) => {
    const instrumentType =
      instrument.instrumentId === CURRENCY_BSV
        ? INSTRUMENT_TYPE_CURRENCY
        : INSTRUMENT_TYPE_CREDIT_NOTE;

    const matchedInstrument = visibleInstruments?.find(
      (match) => match.instrument_id === instrument.instrumentId,
    );

    const contractInstrument = contractDetails[instrument.instrumentId];

    return instrument.withInstrumentDetails({
      ...instrumentDetails[index],
      instrumentId: instrument.instrumentId,
      contractAddress: undefined,
      instrumentType,
      // freezePeriod: contractInstrument.freezePeriod,
      // freezeTxId: contractInstrument.freezeTxId,
      // authorizedQuantity:
      //   contractInstrument.draft?.details?.authorizedQuantity,
      instrumentTypeSpecifics: {
        name: matchedInstrument?.display_name,
        faceValue: {
          units: 1,
          currency: matchedInstrument?.currency_code,
          unitPrecision: matchedInstrument?.max_decimal_places,
        },
        transfersPermitted: false,
      },
      issuerHandle: contractInstrument?.issuerHandle,
      issuerId: contractInstrument?.issuerId,
      issuerName: contractInstrument?.issuerName,
      // contractFee: contract?.draft?.details?.contractFee,
      // transferFee: contractInstrument.draft?.details?.transferFee?.quantity,
      // dateCreated: contractInstrument.draft?.dateCreated,
      // dateModified: contractInstrument.draft?.dateModified,
    });
  });

  instruments = instruments.map((instrument) => {
    const matchedInstrument = visibleInstruments?.find(
      (match) => match.instrument_id === instrument.instrumentId,
    );
    return instrument.withDisplayCurrencyRate(
      matchedInstrument?.rate_currency_code,
      matchedInstrument?.unit_value ?? 1,
    );
  });

  return instruments;
}

export function useAssetsTotal(
  vaultId,
  profileId,
  matchInstruments,
  lockboxId,
) {
  return useLiquidNobleAssetsTotal(
    vaultId,
    profileId,
    matchInstruments,
    lockboxId,
  );
}

export function useRelevantFilteredBalances(options) {
  const { lockboxId } = options ?? {};
  return useLiquidNobleFilteredBalances(lockboxId);
}

export function useLiquidNobleFilteredBalances(lockboxId) {
  const { activeLockboxId } = React.useContext(EntityContext);
  const useLockboxId = lockboxId || activeLockboxId;
  const [balances] = useLockboxBalances(useLockboxId);
  const entityId = useActiveEntityId();

  const instrumentIds = new Set(
    balances?.map(({ instrumentId }) => instrumentId).filter(Boolean),
  );
  const instruments = useLiquidNobleInstrumentListWithDetails([
    ...instrumentIds,
  ]);

  if (!balances) {
    return null;
  }

  const instrumentsById = new Map(
    instruments.map((instrument) => [instrument.instrumentId, instrument]),
  );

  const balanceResults = balances.map((balance) => {
    const instrumentId = balance.instrumentId;
    const instrument = instrumentId
      ? instrumentsById.get(instrumentId)
      : undefined;

    const instrumentDetails = instrument?.instrumentDetails;

    const instrumentType = instrument?.instrumentType;

    // Filter on active/inactive
    const isActive = !instrumentDetails || !!instrumentDetails?.isActive;
    const isExpired = false;
    // Filter on instrument/liability
    const isLiability = !!instrument?.isLiabilityForEntity(entityId);

    const quantities = {};
    if (typeof balance.totalUnits === 'number') {
      quantities.balance = {
        instrumentId: instrument?.instrumentId || instrumentId,
        quantity: balance.totalUnits,
      };
    }
    if (typeof balance.availableUnits === 'number') {
      quantities.available = {
        instrumentId: instrument?.instrumentId || instrumentId,
        quantity: balance.availableUnits,
      };
    }
    if (
      typeof balance.totalUnits === 'number' &&
      typeof balance.availableUnits === 'number' &&
      balance.totalUnits - balance.availableUnits > 0
    ) {
      quantities.reserved = {
        instrumentId: instrument?.instrumentId || instrumentId,
        quantity: balance.totalUnits - balance.availableUnits,
      };
    }
    if (typeof instrumentDetails?.authorizedQuantity === 'number') {
      quantities.authorizedQuantity = {
        instrumentId: instrument?.instrumentId || instrumentId,
        quantity: instrumentDetails.authorizedQuantity,
      };
    }
    if (isLiability) {
      if (
        typeof instrumentDetails?.authorizedQuantity === 'number' &&
        typeof balance.totalUnits === 'number'
      ) {
        quantities.issuedLiability = {
          instrumentId: instrument?.instrumentId || instrumentId,
          quantity: Math.max(
            0,
            instrumentDetails.authorizedQuantity - balance.totalUnits,
          ),
        };
      }
    }

    const instrumentName =
      instrument?.instrumentDetails?.instrumentTypeSpecifics?.name ??
      instrument?.formatInstrumentName();
    const balanceResult = {
      instrumentId,
      instrumentName,
      instrumentType: {
        code: instrumentType,
        formatted: instrumentType,
      },
      instrument,
      isActive,
      isExpired,
      isLiability,
      quantities,
    };
    return balanceResult;
  });

  return balanceResults;
}

export function useCurrentDisplayInstrument() {
  const { displayCurrencyCode } = React.useContext(BankingContext);
  const visibleInstruments = useVisibleInstruments();

  const matchedInstrument = visibleInstruments?.find(
    (match) => match.currency_code === displayCurrencyCode,
  );

  return useLiquidNobleInstrumentWithDetails(matchedInstrument?.instrument_id);
}

export function useCurrencyExchangeRate(currencyCode, useSellRate = false) {
  const visibleInstruments = useVisibleInstruments();

  const matchedInstrument = visibleInstruments?.find(
    (match) => match.currency_code === currencyCode,
  );
  if (!matchedInstrument) {
    return {
      displayCurrencyRate: 1,
      displayCurrencyCode: 'AUD',
    };
  }
  return useSellRate
    ? matchedInstrument.unit_value_sell
    : matchedInstrument.unit_value;
}

export function useDisplayCurrencyExchangeRate(useSellRate = false) {
  const { displayCurrencyCode } = React.useContext(BankingContext);
  const displayCurrencyRate = useCurrencyExchangeRate(
    displayCurrencyCode,
    useSellRate,
  );
  return {
    displayCurrencyRate,
    displayCurrencyCode,
  };
}

export function useLiquidNobleAssetsTotal(
  vaultId,
  profileId,
  matchInstruments,
  lockboxId,
) {
  const { displayCurrencyCode, displayCurrencyRate } =
    useDisplayCurrencyExchangeRate(true);
  const primaryCurrencyCode = 'AUD'; // Hardcoded for now

  const balances = useLiquidNobleFilteredBalances(lockboxId);

  const instrumentOptions =
    matchInstruments?.map((instrument) => ({
      instrumentId: instrument.instrument_id,
      quantity:
        balances?.find(
          (balance) => balance.instrumentId === instrument.instrument_id,
        )?.quantities?.balance?.quantity ?? 0,
    })) ?? [];
  const instrumentBalances =
    matchInstruments === null
      ? balances?.map((balance) => balance.quantities?.balance)
      : instrumentOptions;

  const instruments = useLiquidNobleInstrumentListWithDetails(
    [
      { currencyCode: primaryCurrencyCode, amount: 0 },
      ...(instrumentBalances ?? []),
    ],
    matchInstruments,
  );

  // The first instrument in the array is always
  // a zero amount of the display currency
  const validInstruments =
    instruments?.filter(
      (instrument) => instrument.instrumentId !== undefined,
    ) ?? [];

  if (!validInstruments.length) {
    return null;
  }

  // Find instrument in primaryCurrencyCode to become the totalInstrument
  let totalInstrumentId;
  let totalInstrument = validInstruments.find(
    (instrument) => instrument.currencyCode === primaryCurrencyCode,
  );
  if (!totalInstrument) {
    totalInstrument = validInstruments[0];
  }

  if (totalInstrument) {
    totalInstrumentId = totalInstrument.instrumentDetails?.instrumentId;
    let displayAmount = totalInstrument.toDisplayCurrencyAmount({
      showUnconvertibleAmountAsZero: true,
    });
    if (displayAmount) {
      totalInstrument = displayAmount.withQuantity({
        amount: displayAmount.amount,
      });
    }
  }

  if (!totalInstrument) {
    return null;
  }

  for (let i = 0; i < validInstruments.length; i++) {
    const instrument = validInstruments[i];
    if (!instrument) {
      continue;
    }
    if (!instrument.amount) {
      continue;
    }
    if (
      instrument.currencyCode === primaryCurrencyCode ||
      instrument.instrumentId === totalInstrumentId
    ) {
      continue;
    }
    const displayAmount = instrument.toDisplayCurrencyAmount({
      showUnconvertibleAmountAsZero: true,
    });
    if (!displayAmount) {
      continue;
    }

    totalInstrument = displayAmount.withQuantity({
      amount: totalInstrument.amount + displayAmount.amount,
    });
  }

  // Convert all commodity and fiat instruments converted to AUD to the display currency
  let displayTotalInstrument = totalInstrument?.withDisplayCurrencyRate(
    displayCurrencyCode,
    displayCurrencyRate,
  );
  displayTotalInstrument = displayTotalInstrument?.toDisplayCurrencyAmount();
  return displayTotalInstrument;
}

export function useHasBsv(profileId, minAmount = 0) {
  const vaultId = usePrimaryVault(profileId)?.id;
  const balances = useFilteredBalances({
    profileId,
    vaultId,
    includeInactive: false,
    includeMoney: false,
  });
  if (balances === null) {
    return null;
  }
  const bsv = balances.find((balance) => balance.instrumentId === CURRENCY_BSV);
  if (!bsv) {
    return false;
  }
  const available = bsv.quantities?.available?.quantity ?? 0;
  return available > minAmount;
}
